import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  ReferenceField,
  FilterLiveSearch,
  SelectInput,
  DateInput,
  BooleanInput,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { Chip } from '@mui/material';
import { createEmptyStateComponent } from '../../components/createEmptyStateComponent';

const incidentTypes = [
  { id: 'ACCIDENT', name: 'Accident' },
  { id: 'BREAKDOWN', name: 'Breakdown' },
  { id: 'THEFT', name: 'Theft' },
  { id: 'DAMAGE', name: 'Damage' },
  { id: 'VANDALISM', name: 'Vandalism' },
  { id: 'MAINTENANCE_ISSUE', name: 'Maintenance Issue' },
  { id: 'SAFETY_ISSUE', name: 'Safety Issue' },
  { id: 'COLLISION', name: 'Collision' },
  { id: 'FIRE', name: 'Fire' },
  { id: 'FLOOD', name: 'Flood' },
  { id: 'OTHER', name: 'Other' },
];

const IncidentTypeField = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record?.incidentType) {
    return null;
  }

  const getColor = (type: string) => {
    switch (type) {
      case 'ACCIDENT':
      case 'COLLISION':
      case 'FIRE':
        return 'error';
      case 'BREAKDOWN':
      case 'MAINTENANCE_ISSUE':
        return 'warning';
      case 'THEFT':
      case 'VANDALISM':
        return 'secondary';
      case 'SAFETY_ISSUE':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={translate(
        `resources.equipment_incidents.incident_types.${record.incidentType.toLowerCase()}`,
        {
          defaultValue: record.incidentType.replace(/_/g, ' '),
        }
      )}
      color={getColor(record.incidentType)}
      size="small"
    />
  );
};

const ResolvedField = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (record?.resolved === undefined) {
    return null;
  }

  return (
    <Chip
      label={translate(
        record.resolved
          ? 'resources.equipment_incidents.status.resolved'
          : 'resources.equipment_incidents.status.open',
        {
          defaultValue: record.resolved ? 'Resolved' : 'Open',
        }
      )}
      color={record.resolved ? 'success' : 'warning'}
      size="small"
      variant={record.resolved ? 'filled' : 'outlined'}
    />
  );
};

const CostField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  const value = record?.[source];

  if (!value) {
    return <span>-</span>;
  }

  return <span>${Number(value).toLocaleString()}</span>;
};

const IncidentEmptyState = createEmptyStateComponent('equipment-incidents');

const IncidentListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

const incidentFilters = [
  <FilterLiveSearch key="search" source="description" placeholder="Search incidents..." />,
  <SelectInput
    key="type"
    source="incidentType"
    choices={incidentTypes}
    label="Incident Type"
    alwaysOn
  />,
  <DateInput key="date" source="incidentDate" label="Incident Date" />,
  <BooleanInput key="resolved" source="resolved" label="Resolved Only" />,
  <BooleanInput key="insurance" source="insuranceClaim" label="Insurance Claims" />,
];

export const EquipmentIncidentList = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const translate = useTranslate();

  return (
    <List
      perPage={25}
      sort={{ field: 'incidentDate', order: 'DESC' }}
      filters={incidentFilters}
      actions={<IncidentListActions />}
      empty={<IncidentEmptyState />}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="id" sortable={false} />
        <ReferenceField source="equipmentId" reference="equipment">
          <TextField source="name" />
        </ReferenceField>
        <DateField source="incidentDate" />
        <IncidentTypeField />
        <TextField source="description" />
        <ReferenceField source="driverId" reference="drivers" emptyText="-">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="location" emptyText="-" />
        <CostField source="repairCost" />
        <NumberField source="downtimeDays" emptyText="-" />
        <BooleanField source="insuranceClaim" />
        <ResolvedField />
        <DateField source="resolvedDate" emptyText="-" />
        <DateField source="createdAt" />
      </Datagrid>
    </List>
  );
};
